/**
 * SessionHistoryRail — chat history panel docked to the main sidebar.
 */
import * as React from "react";
import { History, MoreHorizontal, X } from "lucide-react";
import { cn } from "@/lib/cn";
import type { SessionSummary } from "@/hooks/useSessionList";

function fmtRelative(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

function sessionLabel(s: SessionSummary): string {
  if (s.title?.trim()) return s.title.trim();
  if (s.last_message_preview?.trim()) return s.last_message_preview.trim();
  return "New chat";
}

function inboundSurface(sessionId: string): string | null {
  if (!sessionId.startsWith("inbound:")) return null;
  const parts = sessionId.split(":");
  return parts[1] ?? null;
}

function inboundBadgeLabel(surface: string): string {
  const labels: Record<string, string> = {
    telegram: "Telegram",
    slack: "Slack",
    github: "GitHub",
    whatsapp: "WhatsApp",
    discord: "Discord",
  };
  return labels[surface] ?? surface;
}

interface SessionRowProps {
  session: SessionSummary;
  active: boolean;
  onSelect: (id: string) => void;
  onRename: (id: string, title: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

function SessionRow({ session, active, onSelect, onRename, onDelete }: SessionRowProps) {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [renaming, setRenaming] = React.useState(false);
  const [draft, setDraft] = React.useState(session.title ?? "");
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
        setConfirmDelete(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  React.useEffect(() => {
    if (renaming) inputRef.current?.focus();
  }, [renaming]);

  const commitRename = async (): Promise<void> => {
    const next = draft.trim();
    setRenaming(false);
    if (next === (session.title ?? "").trim()) return;
    setBusy(true);
    try {
      await onRename(session.id, next || "");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setBusy(true);
    try {
      await onDelete(session.id);
      setMenuOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={cn(
        "group relative border transition-colors",
        active
          ? "border-accent bg-accent/10 text-fg"
          : "border-transparent hover:border-border hover:bg-bg-elevated text-fg-muted hover:text-fg",
      )}
    >
      {renaming ? (
        <form
          className="px-3 py-2"
          onSubmit={(e) => {
            e.preventDefault();
            void commitRename();
          }}
        >
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => void commitRename()}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setDraft(session.title ?? "");
                setRenaming(false);
              }
            }}
            maxLength={200}
            disabled={busy}
            aria-label="Chat name"
            className="h-7 w-full border border-border bg-bg px-2 text-sm text-fg focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus"
          />
        </form>
      ) : (
        <button
          type="button"
          onClick={() => onSelect(session.id)}
          className="w-full px-3 py-2 pr-9 text-left"
        >
          <div className="flex items-center gap-2 min-w-0">
            {inboundSurface(session.id) && (
              <span className="shrink-0 text-[10px] uppercase tracking-wide px-1 py-0.5 border border-border text-fg-faint">
                {inboundBadgeLabel(inboundSurface(session.id)!)}
              </span>
            )}
            <div className="truncate text-sm font-medium">{sessionLabel(session)}</div>
          </div>
          {session.last_message_preview && session.title && (
            <div className="mt-0.5 truncate text-xs text-fg-faint">
              {session.last_message_preview}
            </div>
          )}
          <div className="mt-1 text-[10px] font-mono text-fg-faint">
            {fmtRelative(session.updated_at)}
          </div>
        </button>
      )}

      <div ref={menuRef} className="absolute right-1 top-1">
        <button
          type="button"
          aria-label="Chat actions"
          aria-expanded={menuOpen}
          disabled={busy || renaming}
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((v) => !v);
            setConfirmDelete(false);
          }}
          className={cn(
            "p-1 text-fg-muted hover:text-fg",
            "opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100",
            menuOpen && "md:opacity-100",
          )}
        >
          <MoreHorizontal size={14} />
        </button>
        {menuOpen && (
          <div className="absolute right-0 z-20 mt-1 min-w-[8.5rem] border border-border bg-bg-elevated py-1 shadow-lg">
            <button
              type="button"
              className="w-full px-3 py-1.5 text-left text-xs text-fg hover:bg-bg-sunken"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen(false);
                setDraft(session.title ?? sessionLabel(session));
                setRenaming(true);
              }}
            >
              Rename
            </button>
            <button
              type="button"
              disabled={busy}
              className={cn(
                "w-full px-3 py-1.5 text-left text-xs hover:bg-bg-sunken",
                confirmDelete ? "text-danger" : "text-fg",
              )}
              onClick={(e) => {
                e.stopPropagation();
                void handleDelete();
              }}
            >
              {confirmDelete ? "Confirm delete" : "Delete"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

interface SessionHistoryRailProps {
  recent: SessionSummary[];
  older: SessionSummary[];
  activeSessionId: string | null;
  loading: boolean;
  sidebarCollapsed: boolean;
  expanded: boolean;
  isMobile: boolean;
  mobileOpen: boolean;
  onMobileClose: () => void;
  onClose: () => void;
  onSelect: (id: string) => void;
  onNewChat: () => void;
  onRename: (id: string, title: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

export function SessionHistoryRail({
  recent,
  older,
  activeSessionId,
  loading,
  sidebarCollapsed,
  expanded,
  isMobile,
  mobileOpen,
  onMobileClose,
  onClose,
  onSelect,
  onNewChat,
  onRename,
  onDelete,
}: SessionHistoryRailProps) {
  const handleNewChat = React.useCallback(() => {
    onNewChat();
    if (isMobile) onMobileClose();
  }, [onNewChat, isMobile, onMobileClose]);

  const handleSelect = React.useCallback(
    (id: string) => {
      onSelect(id);
      if (isMobile) onMobileClose();
    },
    [onSelect, isMobile, onMobileClose],
  );

  const listBody = (
    <div className="flex-1 overflow-y-auto p-2 space-y-4 min-h-0">
      {loading && recent.length === 0 && older.length === 0 ? (
        <div className="space-y-2 px-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-12 bg-bg-sunken animate-pulse border border-border" />
          ))}
          <p className="text-xs text-fg-faint font-mono">Loading chats…</p>
        </div>
      ) : recent.length === 0 && older.length === 0 ? (
        <p className="px-2 text-xs text-fg-muted">No conversations yet.</p>
      ) : (
        <>
          {recent.length > 0 && (
            <section>
              <h3 className="mb-1 px-2 text-[10px] font-mono uppercase tracking-wider text-fg-faint">
                Most recent
              </h3>
              <div className="space-y-0.5">
                {recent.map((s) => (
                  <SessionRow
                    key={s.id}
                    session={s}
                    active={s.id === activeSessionId}
                    onSelect={handleSelect}
                    onRename={onRename}
                    onDelete={onDelete}
                  />
                ))}
              </div>
            </section>
          )}
          {older.length > 0 && (
            <section>
              <h3 className="mb-1 px-2 text-[10px] font-mono uppercase tracking-wider text-fg-faint">
                Earlier
              </h3>
              <div className="space-y-0.5">
                {older.map((s) => (
                  <SessionRow
                    key={s.id}
                    session={s}
                    active={s.id === activeSessionId}
                    onSelect={handleSelect}
                    onRename={onRename}
                    onDelete={onDelete}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );

  const header = (
    <div className="flex items-center justify-between border-b border-border px-3 py-2 flex-shrink-0">
      <div className="flex items-center gap-2 text-sm font-semibold text-fg min-w-0">
        <History size={14} className="flex-shrink-0" />
        <span className="truncate">Chats</span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          type="button"
          onClick={handleNewChat}
          className="text-xs font-mono text-accent hover:underline"
        >
          New
        </button>
        <button
          type="button"
          onClick={onClose}
          className="p-1 text-fg-muted hover:text-fg"
          aria-label="Close chats panel"
          title="Close"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );

  const panelInner = (
    <div className="flex h-full flex-col bg-bg min-h-0">
      {header}
      {listBody}
    </div>
  );

  const innerWidth = sidebarCollapsed ? "w-[calc(15rem-3.5rem)]" : "w-60";

  if (isMobile) {
    if (!mobileOpen) return null;
    return (
      <div className="fixed inset-0 z-40 flex md:hidden">
        <button
          type="button"
          className="absolute inset-0 bg-black/40 transition-opacity duration-200"
          aria-label="Close chat history"
          onClick={onMobileClose}
        />
        <aside className="relative z-50 flex h-full w-[min(280px,85vw)] flex-col border-r border-border bg-bg shadow-lg animate-in slide-in-from-left duration-200">
          {panelInner}
        </aside>
      </div>
    );
  }

  return (
    <aside
      className={cn(
        "relative hidden md:flex flex-shrink-0 flex-col h-full min-h-0 overflow-hidden",
        "transition-all duration-200",
        expanded
          ? cn(innerWidth, "border-r border-border")
          : "w-0 border-r-0 pointer-events-none",
      )}
      aria-hidden={!expanded}
      aria-label="Chat history"
    >
      <div className={cn("flex h-full min-h-0 flex-col bg-bg", innerWidth)}>
        {panelInner}
      </div>
    </aside>
  );
}
